import React, { useState } from 'react';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import './MyPortfolioCapsulesSettings.scss';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import WidgetSettingsNumericValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsNumericValueEditor';
import { SortDirection } from '@instinet-analytics/core-lib';
import {
    getTopic,
    TopicWithConfig,
} from 'widgets/myPortfolioCapsules/topics/Topic';
import { MyPortfolioCapsulesWidgetConfigState } from 'widgets/myPortfolioCapsules/store/config/MyPortfolioCapsulesWidgetConfigState';
import WidgetSettingsRadioGroupEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsRadioGroupValueEditor';
import WidgetSettingsDropdownEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsDropdownEditor';
import WidgetSettingsCheckboxValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsCheckboxValueEditor';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';
import { AnyConfig } from 'widgets/WidgetRegistry';
import { useAvailableTopics } from 'widgets/myPortfolioCapsules/topics/hooks';

export interface MyPortfolioCapsulesContainerSettingsProps {
    config: MyPortfolioCapsulesWidgetConfigState;
    debouncedApplyWidgetConfigChange: (
        delta: Partial<MyPortfolioCapsulesWidgetConfigState>
    ) => void;
}

export default function MyPortfolioCapsulesContainerSettings(
    props: MyPortfolioCapsulesContainerSettingsProps
) {
    const { config, debouncedApplyWidgetConfigChange } = props;
    const { topic: configTopicType } = config;
    const [topicType, setTopicType] = useState(configTopicType);

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();
    const availableTopics = useAvailableTopics();

    const topicDropdownOptions = availableTopics.map((t) => ({
        text: t.description,
        value: t.type,
    }));

    const onDisplayTopicChanged = (nextTopic?: TopicType) => {
        setTopicType(nextTopic);
    };

    const onTopicChange = (
        delta: Partial<MyPortfolioCapsulesWidgetConfigState>
    ) => {
        const nextTopic = getTopic(delta.topic!) as TopicWithConfig<AnyConfig>;
        const nextTopicConfig = nextTopic.getInitialTopicConfig
            ? nextTopic.getInitialTopicConfig()
            : {};
        const nextDelta = { ...nextTopicConfig, ...delta };
        debouncedApplyWidgetConfigChange(nextDelta);
    };

    const topic = topicType && getTopic(topicType);

    return (
        <div>
            <div className="group">
                <WidgetSettingsDropdownEditor
                    className="topic-dropdown"
                    options={topicDropdownOptions}
                    propertyName={'topic'}
                    config={config}
                    label={'Order By'}
                    onChange={onTopicChange}
                    onDisplayValueChange={onDisplayTopicChanged}
                />

                <WidgetSettingsNumericValueEditor
                    className="number-to-show"
                    propertyName={'numberToShow'}
                    config={config}
                    label={'Number of capsules to show'}
                    onChange={debouncedApplyWidgetConfigChange}
                    min={1}
                    max={20}
                />
                <WidgetSettingsDropdownEditor
                    options={[
                        { value: 'allValid', text: 'Include Done' },
                        { value: 'excludingDone', text: 'Exclude Done' },
                        {
                            value: 'onlyDone',
                            text: 'Done Only',
                        },
                    ]}
                    propertyName={'aggregationKey'}
                    className={'done-orders-dropdown'}
                    config={config}
                    label={'Done Orders'}
                    onChange={debouncedApplyWidgetConfigChange}
                />
                {topic?.options?.absolute && (
                    <WidgetSettingsCheckboxValueEditor
                        propertyName={'absoluteValues'}
                        config={config}
                        label={'Absolute Values'}
                        onChange={debouncedApplyWidgetConfigChange}
                    />
                )}
            </div>
            <div className="group">
                <WidgetSettingsRadioGroupEditor
                    config={config}
                    propertyName={'sort'}
                    onChange={debouncedApplyWidgetConfigChange}
                    label={'Sort Direction'}
                    options={[
                        {
                            key: SortDirection.DESCENDING,
                            label: 'Descending',
                        },
                        {
                            key: SortDirection.ASCENDING,
                            label: 'Ascending',
                        },
                    ]}
                />

                <WidgetSettingsRadioGroupEditor
                    config={config}
                    propertyName={'orientation'}
                    onChange={debouncedApplyWidgetConfigChange}
                    label={'Orientation'}
                    options={[
                        {
                            key: 'row',
                            label: 'Rows',
                        },
                        {
                            key: 'column',
                            label: 'Columns',
                        },
                    ]}
                />
            </div>
            {isAppModeNewportSingleWidget && (
                <AppSettingsEditor
                    appSettings={appSettings}
                    onAppSettingsChanged={dispatchUpdateAppSettings}
                    specifyVisibleControls={{
                        symbology: true,
                        showChartHelp: true,
                        showTooltips: true,
                    }}
                />
            )}
            <div className="group">
                <WidgetSettingsCheckboxValueEditor
                    propertyName={'showTitleCapsule'}
                    config={config}
                    label={'Show Title Capsule'}
                    onChange={debouncedApplyWidgetConfigChange}
                />
            </div>
        </div>
    );
}
